import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';


import useOutsideClick from '../../hooks/useOutsideClick';
import './index.scss'; // Assuming you have a CSS file for styles
import { config } from '../../config';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import { openModal } from '../../redux/Slices/modalSlice';
import SearchResults from './SearchResults';
import Logo, { LogoLight, LogoMobile, LogoMobileLight } from './Logo';
import BrandLogo from '../WhiteLabel/BrandLogo';
import { useWhiteLabel } from '../../context/WhiteLabelContext';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import useHandleExpandableColumnClick from '../../hooks/useHandleExpandableColumnClick';
import useDebouncedClick from '../../hooks/useDebouncedClick';
import useNavigationGuard from '../../hooks/useNavigationGuard';

import { setSelectedBackground } from '../../redux/Slices/boardSlice';
import { useTheme } from '../../context/ThemeContext';
import MemberOnlineStatus from '../Global/MemberOnlineStatus';

import Search from '../icons/Search';
import Campaign from '../icons/Campaign';

// Initialize socket outside of the component to prevent re-initialization on each render
import { createSocket } from '../../utils/socketUtils';
import NewLabel from '../icons/NewLabel';
import NotificationsIcon from '../icons/Notifications';
import Home from '../icons/Home';
import Add from '../icons/Add';
import { closeExpandableColumn } from '../../redux/Slices/expandableColumnSlice';
import Workspaces from '../icons/Workspaces';

const socket = createSocket();

// import useOnlineStatus from '../../hooks/useOnlineStatus';


const Sidebar = () => {
  const [isShowProfile, setIsShowProfile] = useState(false);
  const [newNotification, setNewNotification] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [isShowSearchResult, setSearchResult] = useState(false);
  const [query, setQuery] = useState(""); // Search query
  const [activeNav, setActiveNav] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // White label context
  const { isWhiteLabel } = useWhiteLabel();

  const location = useLocation();
  const profileRef = useRef(null);
  const notificationsRef = useRef(null);
  const searchRef = useRef(null);

  const workspaceRef = useRef(null);
  const recentRef = useRef(null);
  const starredRef = useRef(null);
  const navigate = useNavigate();

  const { board } = useSelector((state) => state.board);
  const { theme } = useTheme();

  const { handlePopoverClick } = useHandlePopoverClick();
  const { handleExpandableColumnClick, expandableColumnState } = useHandleExpandableColumnClick();
  const { guardedNavigate, canNavigate } = useNavigationGuard();

  const dispatch = useDispatch();
  const { user, error: userError } = useSelector((state) => state.user);

  const [systemTheme, setSystemTheme] = useState('light'); // Default to light


  useEffect(() => {
    // Check the system theme and set it
    const updateSystemTheme = () => {
      const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setSystemTheme(isDarkMode ? 'dark' : 'light');
    };

    // Initial check
    updateSystemTheme();

    // Add a listener for changes in the system theme
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', updateSystemTheme);

    // Cleanup the event listener on unmount
    return () => {
      mediaQuery.removeEventListener('change', updateSystemTheme);
    };
  }, []);

  // Determine which logo to display
  const currentTheme = theme === 'system' ? systemTheme : theme;


  useEffect(() => {
    if (!user) return;

    const userId = user?.user?._id;
    socket.emit('join', userId);

    const fetchNotificationCount = async () => {
      const count = await getNewNotificationsCount();
      if (count !== undefined) {
        setNewNotification(count);
      }
    };

    fetchNotificationCount();

    socket.on('newNotification', async (notification) => {
      setNewNotification(prevCount => prevCount + 1);
    });

    return () => {
      socket.off('newNotification');
    };
  }, [user]);


  useEffect(() => {

    if (!location.pathname.startsWith('/b/')) {
      dispatch(setSelectedBackground(''));
    }
  }, [location.pathname]);

  const handleShowProfile = () => setIsShowProfile(prev => !prev);


  const handleShowNotifications = (nav, props = {}) => {
    // Reset notification count when opening notifications
    setNewNotification(0);
    addNewNotificationsCount(0);

    setActiveNav(nav);
    handleExpandableColumnClick(nav, props);

  };



  const handleCreateWorkspaceCore = () => {
    dispatch(openModal({ modalType: 'CREATE_WORKSPACE', modalData: {} }));
  };

  const handleCreateWorkspace = useDebouncedClick(handleCreateWorkspaceCore, 300);

  const handleNavClick = (nav, props = {}) => {
    // Set active state immediately for better UX
    setActiveNav(nav);
    handleExpandableColumnClick(nav, props); // Use expandable column instead of popover
  };

  // Sync activeNav with expandable column state
  useEffect(() => {
    if (expandableColumnState.isVisible) {
      setActiveNav(expandableColumnState.contentId);
    } else {
      setActiveNav(null);
    }
  }, [expandableColumnState.isVisible, expandableColumnState.contentId]);

  // Create debounced navigation handlers
  const handleWhiteLabelClickCore = () => {
    const targetPath = `/u/${user?.user?.username}/white-label`;

    if (canNavigate(targetPath)) {
      dispatch(closeExpandableColumn());
      setActiveNav(null);
      guardedNavigate(targetPath);
    }
  };

  const handleHomeClickCore = () => {
    const targetPath = `/u/${user?.user?.username}/boards`;

    if (canNavigate(targetPath)) {
      dispatch(closeExpandableColumn());
      setActiveNav(null);
      guardedNavigate(targetPath);
    }
  };

  const handleWhiteLabelClick = useDebouncedClick(handleWhiteLabelClickCore, 300);
  const handleHomeClick = useDebouncedClick(handleHomeClickCore, 300);


  useOutsideClick(profileRef, () => setIsShowProfile(false));
  useOutsideClick(searchRef, () => {
    setSearchResult(false);
    setIsFocused(false);
  });
  useOutsideClick(workspaceRef, () => setActiveNav(null));
  useOutsideClick(recentRef, () => setActiveNav(null));
  useOutsideClick(starredRef, () => setActiveNav(null));

  const addNewNotificationsCount = async (count) => {
    try {
      const response = await fetch(`${config.API_URI}/v1/notifications/count`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ newNotificationsCount: count }),
      });

      if (!response.ok) throw new Error('Failed to add new notification count');
      await response.json();
    } catch (error) {
      console.error('Error adding notification count:', error);
    }
  };

  const getNewNotificationsCount = async () => {
    try {
      const response = await fetch(`${config.API_URI}/v1/notifications/count`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch notification count');
      const data = await response.json();
      return data.newNotificationsCount;
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  const handleSearchClickCore = () => {
    setSearchResult((prev) => !prev);
  };

  const handleSearchClick = useDebouncedClick(handleSearchClickCore, 200);

  useEffect(() => {
    if (isShowSearchResult) {
      setIsFocused(true);
      searchRef.current.querySelector('input').select();
    }
    else {
      setIsFocused(false);
    }
  }, [isShowSearchResult]);


  // if (userLoading) return null;//<div className='loading'>Loading...</div>;
  if (userError) return <div>Error: {userError}</div>;

  const handleToggleSidebar = () => {
    setIsExpanded(prev => !prev);
  };



  return (
    <>
      <aside className={`zoobbe-global-sidebar ${isExpanded ? 'expanded' : 'collapsed'}`}>
        {/* Logo Section */}
        <div className="sidebar-logo">
          <Link to={`/u/${user?.user?.username}/boards`} className="logo-link">
            {isWhiteLabel() ? (
              <BrandLogo
                size="medium"
                showText={isExpanded}
                useIcon={!isExpanded}
                className="sidebar-brand-logo"
              />
            ) : (
              <>

                {isExpanded ? (
                  <div className="logo-icon">
                    {currentTheme === 'dark' && <LogoMobile height='30px' width='auto' />}
                    {currentTheme === 'light' && <LogoMobile height='30px' width='auto' />}
                  </div>
                ) : (
                  <div className="logo-icon">
                    {currentTheme === 'dark' && <LogoMobile height='30px' width='auto' />}
                    {currentTheme === 'light' && <LogoMobile height='30px' width='auto' />}
                  </div>
                )}
              </>
            )}
          </Link>
          <button
            className="sidebar-toggle"
            onClick={handleToggleSidebar}
            data-tooltip-content={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
            data-tooltip-position="right"
          >
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill={'var(--primary-text-color)'}><path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm120-80v-560H200v560h120Zm80 0h360v-560H400v560Zm-80 0H200h120Z" /></svg>
          </button>
        </div>


        {/* Main Navigation */}
        <nav className="sidebar-nav">
          <ul className="sidebar-items">
            {/* Actions Section */}
            <li
              className="sidebar-item action-item workspaces-page"
              onClick={handleHomeClick}
              data-tooltip-content={!isExpanded ? "Workspaces" : ""}
              data-tooltip-position="right"
            >
              <div className="item-icon">
                <Workspaces size={20} color='var(--brand-color)' />
              </div>
              {isExpanded && <span className="item-label">Workspaces</span>}
            </li>

            <li
              className="sidebar-item action-item white-label"
              onClick={handleWhiteLabelClick}
              data-tooltip-content={!isExpanded ? "White Label" : ""}
              data-tooltip-position="right"
            >
              <div className="item-icon">
                <NewLabel size={20} color='var(--brand-color)' />
                <span className="indicator"></span>
              </div>
              {isExpanded && <span className="item-label">White Label</span>}
            </li>

            {user?.user?.verified && (
              <li
                className="sidebar-item action-item create-workspace"
                onClick={handleCreateWorkspace}
                data-tooltip-content={!isExpanded ? "Create Workspace" : ""}
                data-tooltip-position="right"
              >
                <div className="item-icon">
                  <Add size={20} color='var(--primary-text-color)' />
                </div>
                {isExpanded && <span className="item-label">Create</span>}
              </li>
            )}

            {/* Search Section */}
            <li
              className={`sidebar-item search-trigger ${isShowSearchResult ? 'active' : ''}`}
              onClick={handleSearchClick}
              data-tooltip-content={!isExpanded ? "Search" : ""}
              data-tooltip-position="right"

            >
              <div className="item-icon">
                <Search size={20} />
              </div>
              {isExpanded && <span className="item-label">Search</span>}
            </li>



            <li
              className={`sidebar-item nav-item ${activeNav === 'navRecent' ? 'active' : ''}`}
              id='nav-recent'
              onClick={() => handleNavClick('navRecent', { boardId: board?.shortId })}
              ref={recentRef}
              data-tooltip-content={!isExpanded ? "Recent" : ""}
              data-tooltip-position="right"
            >
              <div className="item-icon">
                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="var(--primary-text-color)">
                  <path d="M480-80q-155 0-269-103T82-440h81q15 121 105.5 200.5T480-160q134 0 227-93t93-227q0-134-93-227t-227-93q-86 0-159.5 42.5T204-640h116v80H88q29-140 139-230t253-90q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm112-232L440-464v-216h80v184l128 128-56 56Z" />
                </svg>
              </div>
              {isExpanded && <span className="item-label">Recent</span>}
            </li>
            <li
              className={`sidebar-item nav-item ${activeNav === 'navStarred' ? 'active' : ''}`}
              id='nav-starred'
              onClick={() => handleNavClick('navStarred', { boardId: board?.shortId })}
              ref={starredRef}
              data-tooltip-content={!isExpanded ? "Starred" : ""}
              data-tooltip-position="right"
            >
              <div className="item-icon">
                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="var(--primary-text-color)">
                  <path d="m363-310 117-71 117 71-31-133 104-90-137-11-53-126-53 126-137 11 104 90-31 133ZM480-28 346-160H160v-186L28-480l132-134v-186h186l134-132 134 132h186v186l132 134-132 134v186H614L480-28Zm0-112 100-100h140v-140l100-100-100-100v-140H580L480-820 380-720H240v140L140-480l100 100v140h140l100 100Zm0-340Z" />
                </svg>
              </div>
              {isExpanded && <span className="item-label">Starred</span>}
            </li>


            <li
              className={`sidebar-item bottom-item ${activeNav === 'notifications' ? 'active' : ''}`}
              ref={notificationsRef}
              id="notifications"
              onClick={() => handleShowNotifications('notifications')}
              data-tooltip-content={!isExpanded ? "Notifications" : ""}
              data-tooltip-position="right"
            >
              <div className="item-icon">
                <NotificationsIcon size={18} />
                {newNotification > 0 && <span className='notification-counter'>{newNotification}</span>}
              </div>
              {isExpanded && <span className="item-label">Notifications</span>}
            </li>


            {/* Bottom Section Items */}
            <li
              className={`sidebar-item bottom-item ${activeNav === 'feedBack' ? 'active' : ''}`}
              onClick={() => handleNavClick('feedBack')}
              data-tooltip-content={!isExpanded ? "Feedback" : ""}
              data-tooltip-position="right"
              id='feedback-button'
            >
              <div className="item-icon">
                <Campaign size={18} />
              </div>
              {isExpanded && <span className="item-label">Feedback</span>}
            </li>

            <li
              className={`sidebar-item bottom-item profile ${isShowProfile ? 'active' : ''}`}
              ref={profileRef}
              id="popover-profile"
              data-popover-trigger
              onClick={(e) => {
                handlePopoverClick(e, 'profile', { userInfo: user, setIsShowProfile });
              }}
              data-tooltip-content={!isExpanded ? user?.user?.username : ""}
              data-tooltip-position="right"
            >
              <div className="profile-avatar">
                {user?.user?.profilePicture ? (
                  <div className="image-placeholder-container">
                    <img
                      className="avatar-image"
                      src={user?.user?.profilePicture}
                      alt={user?.user?.username}
                      loading="lazy"
                      style={{
                        filter: user?.user?.userStatus === 'inactive' ? 'grayscale(100%)' : 'none'
                      }}
                    />
                    {user?.user?.userStatus === 'inactive' && (
                      <span className="inactive-indicator" title="Inactive user">
                        <span className="material-symbols-outlined">schedule</span>
                      </span>
                    )}
                    {user?.user?.userStatus !== 'inactive' && <MemberOnlineStatus member={user?.user} />}
                  </div>
                ) : (
                  <ImagePlaceholder
                    member={user?.user}
                    key={user?.user?._id}
                    size={32}
                    text={user?.user?.username}
                    className={`${isShowProfile ? ' active' : ''}`}
                  />
                )}
              </div>
              {isExpanded && (
                <div className="profile-info">
                  <span className="profile-name">{user?.user?.username}</span>
                  <span className="profile-email">{user?.user?.email}</span>
                </div>
              )}
            </li>
          </ul>
        </nav>



      </aside>
      
      {/* Search Modal */}
      {isShowSearchResult && (
        <div className={`header-center ${isShowSearchResult ? 'active-search' : ''}`}>
          <div className={`zoobbe-header__search-container ${isShowSearchResult ? 'isOpen' : ''} ${isFocused ? 'focused' : ''}`} ref={searchRef}>
            <div className="search-content">
              <Search size={20} />
              <input
                className="zoobbe-header__search-input"
                type="text"
                placeholder="Search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => {
                  setIsFocused(true);
                  setSearchResult(true);
                }} // Add focused class and show SearchResults
                onBlur={() => setIsFocused(false)} // Remove focused class and hide SearchResults
                autoComplete="off"
              />
            </div>
            {isShowSearchResult && <SearchResults boardId={board?.shortId} query={query} setQuery={setQuery} setSearchResult={setSearchResult} />}
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
